import { Coins, MoveRight } from "@tamagui/lucide-icons";
import Big from "big.js";
import { useMemo, useState } from "react";
import { XStack, YStack } from "tamagui";
import {
  FormDataModal,
  type FormDataModalProps,
  FormSheetType,
} from "../../../core/components/FormDataModal";
import { showConfetti } from "../../../core/states.ts/showConfetti";
import { Body } from "../../../ui/Body";
import { UserDisplay } from "../../../ui/UserAvatar";
import type ExpenseModuleModel from "../ExpenseModuleModel";
import type { ExpenseDebt } from "../ExpenseModuleModel";

function getDescription(
  currentAmount: Big,
  deptAmount: number,
  formatter: (value: number) => string,
) {
  const diff = currentAmount.minus(deptAmount);
  const absDiff = diff.abs();

  if (absDiff.lt(0.005)) {
    return "Settle your debt or pay of parts of it.";
  }

  if (diff.gt(0)) {
    return `You have entered an amount that is ${formatter(
      absDiff.toNumber(),
    )} more than the required debt.`;
  }

  return `Your entered amount is short by ${formatter(absDiff.toNumber())}.`;
}

export const DebtModal: React.FC<
  Omit<FormDataModalProps, "values" | "onSave" | "title" | "form"> & {
    module: ExpenseModuleModel;
    debt: ExpenseDebt;
  }
> = (props) => {
  const initialValue = props.debt.amount.round(2).toNumber();
  const exactValue = props.debt.amount.round(5).toNumber();
  const exactAndInitialEqual = initialValue === exactValue;
  const [currentAmount, setCurrentAmount] = useState(
    props.debt.amount.round(5),
  );

  return (
    <FormDataModal
      {...props}
      title={"Settle debt"}
      description={
        <YStack gap="$larger">
          <Body muted>
            {getDescription(
              currentAmount,
              exactValue,
              props.module.currencyFormatter.format,
            )}
          </Body>

          <XStack alignItems="center" justifyContent="space-between">
            <UserDisplay userUid={props.debt.debtorUser?.uid} />
            <MoveRight />
            <UserDisplay userUid={props.debt.creditorUser?.uid} />
          </XStack>
        </YStack>
      }
      buttonSaveText="Settle debt"
      buttonIcon={Coins}
      values={useMemo(
        () => ({
          amount: initialValue,
        }),
        [initialValue],
      )}
      onValuesChange={({ values }) => {
        const amount =
          typeof values.amount === "string"
            ? Number.parseFloat(values.amount?.replaceAll(",", "."))
            : values.amount;

        setCurrentAmount(new Big(amount));
      }}
      form={[
        {
          formKey: "amount",
          placeholder: "Amount",
          type: FormSheetType.Input,
          icon: Coins,
          infoText: exactAndInitialEqual
            ? `The owed amount is ${exactValue}`
            : `The exact owed amount is ${exactValue}`,
        },
      ]}
      onSave={(data) => {
        const amountNumber =
          typeof data.amount === "string"
            ? Number.parseFloat(data.amount?.replaceAll(",", "."))
            : data.amount;

        if (
          !props.module.plan.userInPlan ||
          !amountNumber ||
          Number.isNaN(amountNumber) ||
          !props.debt.creditorUser ||
          !props.debt.debtorUser
        ) {
          return;
        }

        props.module.addSettlement({
          debtorUserId: props.debt.debtorUser?.uid,
          creditorUserId: props.debt.creditorUser?.uid,
          amountPaid:
            initialValue === data.amount
              ? props.debt.amount.toNumber()
              : amountNumber,
        });

        showConfetti();
      }}
    />
  );
};
